import React from 'react';
import { formatNumber, formatPercent, UnitCode } from './reportFormat';

/**
 * Per-property league table for the portfolio summary.
 *
 * The point of this table is that the owner should not have to scroll through a
 * section per property to answer "which unit is carrying the month, and which one
 * is not". Every figure here also appears in that property's own section below —
 * this is the index, not a second source.
 *
 * ── Ordering ─────────────────────────────────────────────────────────────────
 * Gross revenue, descending. The property SECTIONS below are ordered by Arabic
 * name (a stable reading order for a document); a league table sorted the same way
 * would answer nothing. Stated in the caption so the two orders do not read as a
 * bug.
 *
 * ── Occupancy is a number, not a bar ─────────────────────────────────────────
 * An inline proportional meter was tried and removed (Adil, 2026-07-30): at real
 * occupancy levels the bars are near-empty slivers that add ink without adding
 * information, and the column is already scannable as a percentage. The booked /
 * available night counts stay available on hover.
 *
 * Note for anyone tempted to colour this column: don't, without a decision. The
 * ops dashboard scores occupancy against targets; this report deliberately does
 * not (§14.1) — it labels a partial month rather than grading it. A red/amber/green
 * band would reintroduce scoring by the back door, using thresholds nobody has
 * agreed for owner-facing use.
 *
 * ── No movement column ───────────────────────────────────────────────────────
 * Month-over-month was removed here too (Adil, 2026-08-01), for the same reason it
 * left the metric cards: one month against one month is not a trend, and the report
 * now answers direction with the 6-month chart alone. `comparison.*` is still on
 * every snapshot, so restoring the column is a render change only.
 */

const PropertyLeagueTable = ({ properties = [] }) => {
  if (!Array.isArray(properties) || properties.length === 0) return null;

  const rows = [...properties].sort(
    (a, b) => (b.metrics?.revenue?.gross || 0) - (a.metrics?.revenue?.gross || 0)
  );

  return (
    <div className="report-league">
      <h5>أداء كل وحدة</h5>

      {/* Its own scroll container: five columns cannot fit a phone, and the page
          body must never scroll sideways. */}
      <div className="report-league-scroll">
        <table className="report-league-table">
          <thead>
            <tr>
              <th scope="col">الوحدة</th>
              <th scope="col">نسبة الإشغال</th>
              <th scope="col">إجمالي إيرادات الحجوزات (ر.س)</th>
              <th scope="col">متوسط السعر اليومي (ر.س)</th>
              <th scope="col">عدد الحجوزات</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(property => {
              const occupancy = property.metrics?.occupancy || {};
              const rate = occupancy.rate || 0;

              return (
                <tr key={property.propertyId} className={property.hasActivity ? '' : 'is-idle'}>
                  <th scope="row">
                    <span className="league-unit-name">{property.name}</span>
                    {/* Below the name, on its own line — beside it, the code
                        competes with a long Arabic unit name for the column's
                        width and pushes the figures. */}
                    <UnitCode code={property.propertyCode} />
                    {property.coverage?.partialMonth && (
                      <span className="league-partial" title="عقار مُضاف خلال الشهر">
                        مُضاف خلال الشهر
                      </span>
                    )}
                  </th>

                  <td
                    className="league-occupancy"
                    title={`${formatNumber(occupancy.bookedNights || 0)} من ${formatNumber(occupancy.totalNights || 0)} ليلة`}
                  >
                    <span className="league-figure">{formatPercent(rate)}</span>
                  </td>

                  {/* Currency lives in the column header, not in every cell — the
                      suffix repeated 3× per row is noise. The metric CARDS keep it,
                      where a lone figure needs its unit. */}
                  <td className="league-money">{formatNumber(property.metrics?.revenue?.gross || 0)}</td>

                  <td className="league-money">{formatNumber(property.metrics?.rates?.adr || 0)}</td>

                  <td>{formatNumber(property.metrics?.bookings?.total || 0)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="report-league-caption">
        مرتّبة حسب إجمالي إيرادات الحجوزات — من الأعلى إلى الأدنى
      </p>
    </div>
  );
};

export default PropertyLeagueTable;
